
import { ref, reactive } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { importAcceptDir, importDirDesc, sheetKeysMap, songType, songSheetType } from '@/utils/dataMap/song'
import SparkMD5 from 'spark-md5'
import { uploadFileToOss } from '@/utils/oss'
import { createOfficialSheetSong } from '@/services/songs'
import { PlusOutlined } from '@ant-design/icons-vue'
import { xlsxImport, acceptXlsx } from '@/utils/fileUpload/xlsxImport'
import { getVarType, concatWithoutSame } from '@/utils/common'

export default {
  name: 'Import',
  components: {
    PlusOutlined
  },
  setup () {
    const configData = ref(null)
    const CONFIG_URL = 'https://prod-ecoach-file.oss-cn-shenzhen.aliyuncs.com/config-music.xlsx'
    const browsers = ['Chrome', 'Firefox', 'Edge', 'QQ浏览器']
    const ignoreFiles = ['.DS_Store']
    const SONG_NAME_INDEX = 2 // 歌曲识别码的位置
    const allFileList = ref([])
    const errorFiles = ref([])
    const tempFileList = ref([])
    const resources = ref([])
    const paraExcel = ref([])
    const pageState = reactive({
      errorVisible: false,
      canReset: true,
      uploading: false,
      md5: [],
      finish: false,
      configSelected: false
    })

    const showRemoveConfirm = file => {
      Modal.confirm({
        title: '确定删除',
        content: <div>当前文件已上传：{file.percent}%，确认删除？</div>,
        onOk () {
          allFileList.value = allFileList.value.filter(item => item.uid !== file.uid)
        }
      })
    }

    const handleRemove = file => {
      const { status } = file
      switch (status) {
      case 'uploading':
        showRemoveFileConfirm(file, '文件正在上传，确定删除？')
        break
      case 'done':
        showRemoveFileConfirm(file, '文件已上传成功，确定删除？')
        break
      default:
        allFileList.value = allFileList.value.filter(item => item.uid !== file.uid)
        break
      }
    }

    // 删除文件时的提示
    const showRemoveFileConfirm = (file, title) => {
      confirm({
        title,
        content: `文件：${file.webkitRelativePath}`,
        okText: '确定',
        cancelText: '取消',
        width: 600,
        onOk: () => {
          allFileList.value = allFileList.value.filter(item => item.uid !== file.uid)
        }
      })
    }

    const beforeUpload = (file, fileList) => {
      // 检查当文件
      if (!fileCheck(file)) {
        errorFiles.value.push(file) // 不可上传的文件
      } else {
        tempFileList.value.push(file) // 可上传文件
      }

      // 到最后一个文件的时候，再做一些处理
      if (fileList.indexOf(file) === fileList.length - 1) {
        const concatKey = 'webkitRelativePath'
        //! 以 allFileList 为准，因此 allFileList 在前面
        const result = concatWithoutSame(concatKey, allFileList.value, tempFileList.value)
        tempFileList.value = [] //* 置空临时文件列表
        allFileList.value = result

        // 存在不可上传的文件时，展示 error modal
        if (errorFiles.value.length > 0) {
          pageState.errorVisible = true
        }
      }
      return false
    }

    const handleErrorsCancel = () => {
      pageState.errorVisible = false
      errorFiles.value = []
    }

    const handleReset = () => {
      allFileList.value = []

      selectConfigFile.value = null
      pageState.configSelected = false
      message.success('数据重置成功！')
    }

    const complexUpload = async index => {
      await uploadWithMd5(index)

      if (index + 1 >= allFileList.value.length) {
        pageState.finish = true
        pageState.uploading = false
        message.success('文件已上传，请点击“创建”按钮保存')
        return
      }

      return complexUpload(index + 1)
    }

    const handleUpload = () => {
      pageState.canReset = false
      pageState.uploading = true
      complexUpload(0)
    }

    const uploadWithMd5 = async index => {
      const file = allFileList.value[index]

      // 跳过已经上传的文件
      if (file.status === 'done') return

      const blobSlice =
        File.prototype.slice ||
        File.prototype.mozSlice ||
        File.prototype.webkitSlice
      const chunkSize = 2097152 // Read in chunks of 2MB
      const chunks = Math.ceil(file.size / chunkSize)
      const spark = new SparkMD5.ArrayBuffer()
      const reader = new FileReader()
      let currentChunk = 0

      function loadNext () {
        const start = currentChunk * chunkSize

        const end =
          start + chunkSize >= file.size ? file.size : start + chunkSize

        reader.readAsArrayBuffer(blobSlice.call(file, start, end))
      }

      reader.onload = e => {
        spark.append(e.target.result) // Append array buffer
        currentChunk += 1
        if (currentChunk < chunks) {
          loadNext()
        } else {
          pageState.md5.push([file.webkitRelativePath, spark.end()])
        }
      }

      loadNext()

      try {
        const data = await uploadFileToOss(file, true, undefined, '', false)

        file.percent = 100
        file.status = 'done'
        file.url = data.url
        file.md5 = data.md5

        allFileList.value = allFileList.value.slice()

        // 文件上传结束，下面将上传后的文件处理到 resources 数组里
        const mp3_file_md5 = pageState.md5.find(
          item => item[0] === file.webkitRelativePath
        )[1]
        // 以文件所在的文件夹作为 dir 标记，不要携带文件名
        const splits = file.webkitRelativePath.split('/')
        splits.pop() // 去除文件名
        const mp3File = {
          dir: splits.join('/'),
          filename: file.name, // 记下文件名，后面会通过这个来对 mp3 排序
          mp3_file_url: file.url,
          mp3_file_md5,
          para_excel_url: undefined
        }

        const song_name = file.webkitRelativePath.split('/')[SONG_NAME_INDEX]
        const resourceIndex = resources.value.findIndex(
          item => String(item.song_name) === String(song_name)
        )
        if (resourceIndex > -1) {
          // 该歌曲已存在
          resources.value[resourceIndex].mp3_file_urls.push(mp3File)
        } else {
          // 该歌曲不存在
          const item = {
            song_name,
            song_type: 1,
            mp3_file_urls: [mp3File],
            picture_urls: []
          }
          resources.value.push(item)
        }
      } catch (e) {
        message.error('上传出错')
        console.error(e)
      }
    }

    // 整理上传完成的文件
    const prepareFiles = fileList => {
      fileList.forEach(file => {
        const filePath = file.webkitRelativePath.toLowerCase()
        const songCode = getSongCode(filePath)
        const configItem = configData.value[songCode]
        if (!configItem) {
          throw new Error('数据处理错误，请检查配置文件')
        }
        if (filePath.includes('acc')) {
          // 伴唱歌曲文件
          configItem.acc_file_md5 = file.md5
          configItem.acc_file_url = file.url
          configItem.acc_file_size = file.size
        } else if (filePath.includes('ori')) {
          // 原唱歌曲文件
          configItem.ori_file_md5 = file.md5
          configItem.ori_file_url = file.url
          configItem.ori_file_size = file.size
        } else if (filePath.includes('evxml') || filePath.includes('musicxml')) {
          // evxml 曲谱文件，目前只支持上传一个，因此只设置第一个的值就可以了
          configItem.evxml_file_url = file.url
          configItem.evxml_file_size = file.size
          configItem.evxml_file_md5 = file.md5
        } else if (filePath.includes('image')) {
          // 图片文件，直接将链接加到对应字段里
          configItem.picture_url.push(file.url)
        } else if (filePath.includes('evms')) {
          // evxml 曲谱文件，目前只支持上传一个，因此只设置第一个的值就可以了
          configItem.evms_file_url = file.url
          configItem.evms_file_size = file.size
          configItem.evms_file_md5 = file.md5
        }
      })
      return Object.values(configData.value)
    }

    const handleBatch = async () => {
      pageState.loading = true
      let resources

      try {
        resources = prepareFiles(allFileList.value)

        if (!resources) return
        for (const k in resources) {
          const item = resources[k]
          if (item.acc_file_url && !item.acc_speed) {
            throw new Error(`《${item.name}》的伴唱速度不能为空`)
          }
          if (item.acc_file_url && item.acc_file_url.length > 256) {
            throw new Error(`《${item.name}》的伴唱文件url过长`)
          }
          if (item.ori_file_url && item.ori_file_url.length > 256) {
            throw new Error(`《${item.name}》的原唱文件url过长`)
          }
          if (item.evxml_file_url && item.evxml_file_url.length > 256) {
            throw new Error(`《${item.name}》的曲谱文件url过长`)
          }
          if (item.evms_file_url && item.evms_file_url.length > 256) {
            throw new Error(`《${item.name}》的evms文件url过长`)
          }
          await createOfficialSheetSong(resources[k])
        }
        pageState.loading = false
        message.success('批量导入成功')
        history.go(-1)
      } catch (error) {
        message.error(error.message)
        console.error(error)
      }
    }

    // 从文件的相对路径中获取歌曲识别码
    const getSongCode = filePath => {
      let songCode
      const pathSplit = filePath.split('/')
      const filename = pathSplit.pop()
      const fileDir = pathSplit[1]?.toLowerCase() // importAcceptDir 中的一个值

      // 原伴唱文件
      if (fileDir === 'acc' || fileDir === 'ori') [songCode] = filename.split('-')

      // 曲谱文件
      if (fileDir === 'evxml' || fileDir === 'musicxml' || fileDir === 'evms') {
        [songCode] = filename.split('.')
      }

      // 歌曲图片
      if (fileDir === 'image') [, , songCode] = pathSplit

      return songCode
    }

    // 检查文件路径的合法性，true 合法, false 非法
    const fileCheck = file => {
      if (!configData.value) return false

      const filePath = file.webkitRelativePath
      const pathSplit = filePath.split('/')
      const fileDir = pathSplit[1].toLowerCase() // importAcceptDir 中的一个值
      // 从文件的相对路径中获取歌曲识别码
      const songCode = getSongCode(filePath)
      // 没有读取到 songCode, 不能上传
      if (songCode === undefined) return false

      const current = configData.value[songCode]

      // 没有读到配置文件的信息，不能上传
      if (current === undefined) return false
      // 图片、原伴唱文件，允许上传。没有做图片的格式检测
      if (fileDir === 'acc' || fileDir === 'ori' || fileDir === 'image') {
        return true
      }
      if (fileDir === 'evxml') {
        return true
      }
      if (fileDir === 'musicxml') {
        return true
      }
      if (fileDir === 'evms') {
        return true
      }

      // ! 其它文件默认不允许上传，如需扩展，请修改上文的判断
      return false
    }

    // 整理配置文件的内容
    const prepareConfigData = sheets => {
      // 只读取第 1 个 sheet 中的内容
      const sheetRawData = Object.values(sheets)[0]
      const sheetData = {}
      sheetRawData.forEach(sheetItem => {
        const item = {}
        const keys = Object.keys(sheetKeysMap)
        keys.forEach(key => {
          // key: 对应 Excel 表中的表头
          // sheetKeysMap[key]: 对应接口字段
          item[sheetKeysMap[key]] = sheetItem[key]
          if (sheetKeysMap[key] === 'acc_speed' && item[sheetKeysMap[key]] === '') {
            item[sheetKeysMap[key]] = null
          }
        })
        // 整理曲谱文件版本相关内容
        const { ...value } = item

        // 排除空白数据的干扰
        if (!value.song_code) return
        if (getVarType(value.song_code) === 'string') {
          const code = value.song_code.replaceAll(' ', '')
          if (code === '') return
        }

        // 初始化 picture_url 字段
        value.picture_url = []
        sheetData[value.song_code] = value
      })

      configData.value = sheetData
      message.success('配置文件读取完成，请选择需要上传的文件夹')
    }

    // 选择配置文件
    const selectConfigFile = file => {
      // 没有文件时，表示清空配置文件信息
      if (!file) {
        configData.value = null
        return
      }
      const getDataFromConfigFile = () => {
        xlsxImport(file)
          .then(prepareConfigData)
          .catch(console.error)
        pageState.configSelected = true
      }

      if (configData.value !== null) {
        Modal.confirm({
          centered: true,
          maskClosable: false,
          title: '注意',
          content:
            '当前已存在配置文件数据，重新选择将覆盖原配置文件的内容，是否确认？',
          okText: '确认覆盖',
          onOk: () => getDataFromConfigFile()
        })
        return
      }

      getDataFromConfigFile()
    }

    return {
      songType,
      songSheetType,
      acceptXlsx,
      importAcceptDir,
      importDirDesc,
      errorFiles,
      pageState,
      CONFIG_URL,
      browsers,
      history,
      allFileList,
      handleRemove,
      beforeUpload,
      handleErrorsCancel,
      handleReset,
      handleUpload,
      handleBatch,
      selectConfigFile
    }
  }
}
